import {SKILL_MD_PATH} from 'llm/chat-library/shared/SkillFileUtils';
import {ManageSkillToolCall, SkillFileSource} from 'llm/chat-library/tool-details/ParsedProgressToolCall';

/**
 * A single skill file (SKILL.md or a bundled/loaded file) to render.
 *
 * `content` is the inline text to render. For ManageSkill inputs whose content is provided by
 * artifact reference, `content` is empty and `artifactId` is set — the preview resolves it to text
 * before rendering (see `useResolvedSkillFiles`). LoadSkill files are always inline (no artifactId).
 */
export interface SkillFile {
  path: string;
  content: string;
  artifactId?: string;
}

/**
 * Unified parsed response shape shared by both LoadSkill and ManageSkill.
 *
 * - ManageSkill populates `files` from the tool call input args (the
 *   SKILL.md content and bundled files the agent is writing).
 * - LoadSkill populates `files` from the response body (the file(s) the
 *   backend returned).
 */
export interface ParsedSkillResponse {
  /** False if the tool reported an explicit error. */
  success: boolean;
  skillId: string | null;
  skillName: string | null;
  /** Files with content to render. */
  files: Array<SkillFile>;
  /** Error text or informational note. Empty string when there is none. */
  message: string;
}

export interface ParsedSkillFrontmatter {
  name: string | null;
  description: string | null;
  body: string;
}

/**
 * Parse the ManageSkill XML response into a ParsedSkillResponse.
 *
 * The backend wraps results in `<result>...</result>` on success and
 * `<error>...</error>` on failure (see manage_skill_tool.py). The response
 * itself only confirms success + lists paths; file *contents* come from the
 * tool call input args, so pass `toolCall` to populate `files`. When omitted
 * (e.g. for the inline progress summary) `files` is left empty.
 */
export function parseManageSkillResponse(
  rawResponse: string,
  toolCall?: ManageSkillToolCall
): ParsedSkillResponse | null {
  const resultMatch = rawResponse.match(/<result>\n?([\s\S]*?)\n?<\/result>/);
  if (resultMatch) {
    const body = resultMatch[1].trim();
    const skillAttrs = body.match(/<skill\s+id="([^"]*)"\s+name="([^"]*)"[^/]*\/>/);
    return {
      success: true,
      skillId: skillAttrs?.[1] ?? null,
      skillName: skillAttrs?.[2] ?? null,
      files: toolCall ? getManageSkillFiles(toolCall) : [],
      message: body
        .replace(/<skill[^/]*\/>/, '')
        .replace(/<files>[\s\S]*?<\/files>/, '')
        .trim(),
    };
  }

  const errorMatch = rawResponse.match(/<error>\n?([\s\S]*?)\n?<\/error>/);
  if (errorMatch) {
    return {
      success: false,
      skillId: null,
      skillName: null,
      files: [],
      message: errorMatch[1].trim(),
    };
  }

  return null;
}

/**
 * Parse a SKILL.md file into its frontmatter name/description fields and
 * markdown body. When the content has no YAML frontmatter, the entire
 * content is returned as `body` and the fields are null.
 *
 * This intentionally does not parse YAML generically. We only extract the
 * frontmatter fields the UI renders (`name` and `description`) and treat the
 * rest of the block as opaque text.
 *
 * This is used to render the name/description separately from the rest of the skill instructions/content.
 */
export function parseSkillFrontmatter(skillContent: string): ParsedSkillFrontmatter {
  const match = skillContent.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) {
    return {name: null, description: null, body: skillContent};
  }

  const [, frontmatter, body] = match;
  return {
    name: extractFrontmatterField(frontmatter, 'name'),
    description: extractDescription(frontmatter),
    body: body.trimStart(),
  };
}

/**
 * Extract a single inline top-level SKILL.md frontmatter field.
 */
function extractFrontmatterField(frontmatter: string, key: 'name' | 'description'): string | null {
  return frontmatter.match(new RegExp(`^${key}:\\s*([^\\n]*)$`, 'm'))?.[1].trim() ?? null;
}

function extractDescription(frontmatter: string): string | null {
  const descriptionValue = extractFrontmatterField(frontmatter, 'description');
  if (!descriptionValue) {
    return null;
  }
  if (!/^>[+-]?$/.test(descriptionValue)) {
    return descriptionValue;
  }

  const descriptionBlock = frontmatter.match(/^description:\s*>[+-]?\r?\n((?:[ \t]+.*(?:\r?\n|$))*)/m)?.[1];
  if (!descriptionBlock) {
    return null;
  }

  return descriptionBlock
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join(' ');
}

/**
 * Extract the `name` field from a SKILL.md YAML frontmatter block.
 * Returns null when the content has no frontmatter or no name field.
 */
export function parseSkillNameFromContent(skillContent: string): string | null {
  return parseSkillFrontmatter(skillContent).name;
}

/** Whether the SKILL.md content has a source (inline text or an artifact id). */
function skillMdHasContent(toolCall: ManageSkillToolCall): boolean {
  return toolCall.skillContent.trim().length > 0 || toolCall.skillContentArtifactId != null;
}

/** Build a renderable {@link SkillFile} from a content source (inline text or artifact id). */
function skillFileFromSource(path: string, source: SkillFileSource): SkillFile {
  if (source.artifactId != null) {
    return {path, content: '', artifactId: source.artifactId};
  }
  return {path, content: source.text ?? ''};
}

/**
 * Get the ordered list of files touched by a ManageSkill tool call.
 *
 * SKILL.md is included first when the agent provided content for it (inline text or an artifact
 * id); an edit that only touches bundled files omits SKILL.md from the rendered list. Each file's
 * content is either inline text or an artifact reference (`artifactId`) resolved before rendering.
 */
export function getManageSkillFiles(toolCall: ManageSkillToolCall): Array<SkillFile> {
  const files: Array<SkillFile> = [];
  if (skillMdHasContent(toolCall)) {
    files.push(
      skillFileFromSource(SKILL_MD_PATH, {
        text: toolCall.skillContent,
        artifactId: toolCall.skillContentArtifactId,
      })
    );
  }
  for (const [path, source] of Object.entries(toolCall.skillFiles)) {
    files.push(skillFileFromSource(path, source));
  }
  return files;
}

/**
 * Build the structured lines shown in the Input section for a ManageSkill
 * tool call: the action (Created/Edited), skill name, and files touched.
 */
export function formatManageSkillInput(toolCall: ManageSkillToolCall): string {
  const lines: Array<string> = [];
  const actionLabel = toolCall.action === 'edit' ? 'Edited' : 'Created';
  lines.push(`Action: ${actionLabel}`);

  const name = parseSkillNameFromContent(toolCall.skillContent);
  if (name) {
    lines.push(`Name: ${name}`);
  }

  const files = getManageSkillFiles(toolCall).map((f) => f.path);
  if (files.length > 0) {
    lines.push('Files:');
    for (const path of files) {
      lines.push(`  - ${path}`);
    }
  }

  return lines.join('\n');
}
